import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { CheckIcon, CloseCircleIcon, DragIcon, MagicIcon, PenIcon, PlusIcon } from '../../../../../assets/icons';
import { Dropdown, Input, message, Popconfirm, Typography } from 'antd';

import {
  ActionButtonEnum,
  ActionEnum,
  IActionsConfig,
  IActionsConfigItems,
  IActionsConfigPositions,
  IActionsType,
  IBaseConfigs,
  LiveObjectsIcons,
  MenuItem,
  NewActionsItem,
  ScenarioBlock,
  WhiteboardPreview
} from '../../../../../types';
import { useScriptForm } from '../../../ScriptCreateStore';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNoteActions } from '../../../hooks';
import { DeleteFilled } from '@ant-design/icons';
import { usePreview } from '../../../../../context/PreviewContext';

const NotesContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 16px 70px 16px 50px;
  background-color: var(--light-violet);
  border: 2px solid var(--primary-color);
  border-radius: 8px;
  box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.07);
  min-height: 209px;

  .drag-icon-absolute {
    position: absolute;
    top: 16px;
    left: 16px;
    color: var(--primary-color);
  }

  .pen-icon-absolute {
    position: absolute;
    top: 16px;
    right: 42px;
    color: var(--primary-color);
  }
  .delete-icon-absolute {
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 22px;
    color: var(--primary-color);
  }
`;

const StyledCloseCircleIcon = styled(CloseCircleIcon)`
  position: absolute;
  top: 16px;
  right: 26px;
  height: 32px;
  width: 32px;
  border-radius: 50%;
  background: var(--light-gray);
  color: var(--hint-color);
  cursor: pointer;
`;

const StyledCheckIcon = styled(CheckIcon)`
  position: absolute;
  right: 16px;
  bottom: 16px;
  height: 52px;
  width: 52px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--primary-color);
  cursor: pointer;
`;

const TextAreaContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
`;
const StyledTextArea = styled(Input.TextArea)`
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 23.76px;
  color: var(--primary-color);
  padding-right: 70px;
  background: var(--light-violet);

  &.ant-input,
  &.ant-input:focus,
  &.ant-input:focus-within,
  &.ant-input:hover,
  &.ant-input:active {
    background: var(--light-violet) !important;
  }

  &:hover {
    background-color: var(--light-violet);
  }
`;

const StyledRightButtonSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 58px;
  margin-left: auto;
  margin-right: -1px;
  border-bottom-right-radius: 50px;
  border-top-right-radius: 50px;
  background: #fdedd6;
`;

const ButtonAddAction = styled.button`
  display: flex;
  align-items: center;
  font-family: 'Mulish', sans-serif;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  width: 238px;
  height: 51px;
  padding: 0 0 0 28px;
  background: #ff8832;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  margin-top: auto;
  &:disabled {
    background: #c6c1d2;
    color: var(--text-secondary);
    box-shadow: none;
    cursor: default;
    pointer-events: none;
  }

  &:hover {
    filter: brightness(110%);
  }

  @media (max-width: 443px) {
    width: 100%;
    padding: 0 0 0 24px;
  }
`;

const StyledIcon = styled(MagicIcon)`
  margin-right: 15px;
  @media (max-width: 370px) {
    margin-right: 8px;
  }
`;

const NotesBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px 10px;
  background: #fdedd6;
  color: #ff8832;
  border-radius: 16px;
  padding: 8px 16px;
  font-weight: 800;
  width: fit-content;
  @media (max-width: 830px) {
    gap: 16px 10px;
  }
`;

const ButtonAction = styled.button`
  display: flex;
  align-items: center;
  font-family: 'Mulish', sans-serif;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  width: fit-content;
  height: 25px;
  padding: 0 8px;
  background: #ff8832;
  border-radius: 8px;
  border: none;
  cursor: pointer;

  &:hover {
    filter: brightness(110%);
  }

  & svg {
    margin-right: 4px;
    path {
      fill: #fff;
    }
  }

  &:disabled {
    pointer-events: none;
  }

  @media (max-width: 443px) {
    height: auto;
    padding: 4px 8px;
  }
`;

const ImageActions = styled.img`
  width: 80px;
  height: 80px;
`;

const BlockActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  margin-top: 0;
`;

const ArrowDownIcon = styled.div<{ open: boolean }>`
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid white;
  margin-left: 11px;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.3s;
`;

const StyledContentPreview = styled(Typography.Paragraph)`
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 23.76px;
`;

interface Props {
  block: ScenarioBlock;
  scenarioId: string;
  actionsConfig: IActionsConfig;
  baseActionPaths: IBaseConfigs;
  expandedChapter: string | null;
  editableBlockId: string | null;
  isShowWhiteboardMenu: boolean;
  isShowOnlyWhiteboardOff: boolean;
  liveObjectsBasePath: string;
  liveObjectsIcons: LiveObjectsIcons;
  setEditableBlockId: React.Dispatch<React.SetStateAction<null | string>>;
  setWhiteboardPreview: React.Dispatch<React.SetStateAction<WhiteboardPreview>>;
  setWhiteboardEditableBlock: React.Dispatch<React.SetStateAction<WhiteboardPreview>>;
}

export const Note: React.FC<Props> = ({
  block,
  scenarioId,
  actionsConfig,
  baseActionPaths,
  expandedChapter,
  editableBlockId,
  isShowWhiteboardMenu,
  isShowOnlyWhiteboardOff,
  liveObjectsBasePath,
  liveObjectsIcons,
  setEditableBlockId,
  setWhiteboardPreview,
  setWhiteboardEditableBlock
}) => {
  const {
    formValues: { scenario: scenarioState, live_objects: liveObjects, slides },
    setFormValues,
    setCurrentSection
  } = useScriptForm();
  const { content, actions, id: blockId } = block;
  const actionEntries = Object.entries(actionsConfig);
  const [newContent, setNewContent] = useState(content);
  const [editing, setEditing] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: boolean }>({});
  const [selectedAction, setSelectedAction] = useState<string[]>([]);
  const { setActiveBlockId } = usePreview();
  const initNewActions = () => {
    if (actions?.length) {
      return actions.map(action => {
        const { action_type, id, group, position_x, position_y, size, uniq_id, live_object_id } = action;

        const result: NewActionsItem = {
          action_type,
          id,
          dropdownKey: `${action_type}-${group}`
        };

        if (position_x !== undefined) result.position_x = position_x;
        if (position_y !== undefined) result.position_y = position_y;
        if (size !== undefined) result.size = size;
        if (uniq_id !== undefined) result.uniq_id = uniq_id;
        if (live_object_id !== undefined) result.live_object_id = live_object_id;

        return result;
      });
    }
    return [];
  };

  const [newActions, setNewActions] = useState<NewActionsItem[]>(() => initNewActions());

  const [newActionsToPreview, setNewActionsToPreview] = useState<NewActionsItem[]>([]);
  useEffect(() => {
    const updatedActions = [...newActions];

    const awardsSet = new Set<string>();
    const effectsSet = new Set<string>();

    newActions.forEach(action => {
      if (action.action_type === 'awards') {
        awardsSet.add(action.dropdownKey.split('-')[1]);
      } else if (action.action_type === 'effects') {
        effectsSet.add(action.dropdownKey.split('-')[1]);
      }
    });

    newActions.forEach(action => {
      const group = action.dropdownKey.split('-')[1];
      if (action.action_type === 'awards' && !effectsSet.has(group)) {
        updatedActions.push({
          action_type: 'effects',
          id: '',
          dropdownKey: `effects-${group}`
        });
      } else if (action.action_type === 'effects' && !awardsSet.has(group)) {
        updatedActions.push({
          action_type: 'awards',
          id: '',
          dropdownKey: `awards-${group}`
        });
      }
    });
    updatedActions.sort((a, b) => {
      const groupA = a.dropdownKey.split('-')[1];
      const groupB = b.dropdownKey.split('-')[1];
      return parseInt(groupA) - parseInt(groupB);
    });
    if (!editing) {
      setSelectedAction(updatedActions.map(action => action.action_type).filter(item => item !== 'effects'));

      setNewActionsToPreview(updatedActions);
    }
  }, [newActions, editing]);

  const menuItems: MenuItem[] = actionEntries
    .map(([key, value]) => ({
      key: key,
      label: ActionEnum[key as keyof typeof ActionEnum]
    }))
    .filter(item => item.label && item.key !== 'effects');

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: blockId });
  const [selectedActionsItem, setSelectedActionsItem] = useState<string[]>([]);
  const { menuActions, actionIcons, renderMenuActionsItems, renderLoMenuActionsItems } = useNoteActions({
    menuItems,
    actionsConfig,
    baseActionPaths,
    isShowWhiteboardMenu,
    isShowOnlyWhiteboardOff,
    blockId,
    liveObjects,
    liveObjectsBasePath,
    newActions,
    slides,
    setSelectedAction,
    setNewActions,
    setWhiteboardEditableBlock,
    setWhiteboardPreview,
    setCurrentSection
  });

  useEffect(() => {
    setActiveBlockId(blockId);
  }, [blockId, scenarioState]);

  useEffect(() => {
    const newActionsWhiteboard = newActions
      .filter(item => item.action_type === 'whiteboard')
      .map(item => ({ action: item.id, dropdownKey: item.dropdownKey }));
    setWhiteboardPreview(prev => {
      const updatedWhiteboard = {
        ...(prev.whiteboard || []),
        [blockId]: newActionsWhiteboard.map((item, idx) => ({
          action: item.action,
          slideIdx: idx,
          blockId: blockId,
          dropdownKey: `whiteboard-${item.dropdownKey}`
        }))
      };
      return {
        ...prev,
        whiteboard: updatedWhiteboard
      };
    });
  }, []);

  useEffect(() => {
    let allSelectedItems = document.querySelectorAll('.selected');
    allSelectedItems.forEach(item => item.classList.remove('selected'));
    const validClassNames = selectedActionsItem.filter(className => className.trim() !== '');
    if (validClassNames.length === 0) return;
    const selectedItems = validClassNames.flatMap(className => Array.from(document.querySelectorAll(`.${className}`)));
    if (selectedActionsItem.length) {
      if (Object.values(openDropdowns).filter(item => item).length) {
        selectedItems.forEach(item => item.classList.add('selected'));
      } else {
        setSelectedActionsItem([]);
      }
    }
  }, [openDropdowns, selectedActionsItem]);
  const handleSaveNote = useCallback(() => {
    if (!newActions.length && !newContent) {
      return message.error('Notes cannot be empty');
    }
    const dataToSave = newActions
      .filter(item => !(item.action_type === 'live_object' && item.id === 'place_live_objects' && !item.uniq_id))
      .map(item => {
        return {
          ...item
        };
      });
    if (!dataToSave.length && !newContent) {
      return message.error('Notes cannot be empty');
    }
    const updatedScenarioItems = scenarioState.map((scenario, index) => {
      if (scenario.id === scenarioId) {
        const updatedScenario = { ...scenario };
        updatedScenario.blocks = updatedScenario.blocks.map(block => {
          const actionsToUpdate = dataToSave.map(action => {
            const groupeId = action.dropdownKey.split('-')[1];
            if (action.action_type === 'live_object' && action.id === 'place_live_objects') {
              return {
                action_type: action.action_type,
                id: action.id,
                group: groupeId,
                position_x: action.position_x,
                position_y: action.position_y,
                size: action.size,
                uniq_id: action.uniq_id,
                live_object_id: action.live_object_id
              };
            }
            return {
              action_type: action.action_type,
              id: action.id,
              group: groupeId
            };
          });
          return block.id === blockId ? { ...block, content: newContent, actions: actionsToUpdate || [] } : block;
        });
        return updatedScenario;
      } else {
        return scenario;
      }
    });
    setFormValues(prevState => ({ ...prevState, scenario: updatedScenarioItems }));
    setEditing(false);
    setEditableBlockId(null);
    setActiveBlockId(null);
  }, [scenarioState, scenarioId, blockId, setFormValues, newContent, newActions]);
  const handleRemove = () => {
    const updatedScenarioItems = scenarioState.map(scenario => {
      if (scenario.id === scenarioId) {
        const updatedBlocks = scenario.blocks.filter(block => block.id !== blockId);
        return { ...scenario, blocks: updatedBlocks };
      } else {
        return scenario;
      }
    });

    setFormValues(prevState => ({ ...prevState, scenario: updatedScenarioItems }));
    setActiveBlockId(null);
  };

  const renderIcon = useCallback(
    (actionType: IActionsType, dropdownKey: string) => {
      let dropdownKeyToRender = dropdownKey;
      if (actionType === 'effects') {
        dropdownKeyToRender = dropdownKey.replace('awards', 'effects');
      }

      const selectedActionFromConfig = actionsConfig[actionType as keyof typeof actionsConfig];
      const selectedActionId = newActions.find(item => item.dropdownKey === dropdownKeyToRender)?.id;
      if ((actionType === 'drawing' || actionType === 'whiteboard' || actionType === 'live_object') && selectedActionId) {
        return actionIcons[selectedActionId];
      }
      const filteredConfig = selectedActionFromConfig as (IActionsConfigItems | IActionsConfigPositions)[];

      const icon = selectedActionId && filteredConfig.find(item => item.id === selectedActionId)?.icon;

      if (icon) {
        return (
          <ImageActions
            style={{ width: '25px', height: '25px', marginRight: '4px' }}
            key={icon}
            src={baseActionPaths.actions_media_base_path + icon + '.png'}
            alt={icon}
          />
        );
      }
      return actionIcons[actionType];
    },

    [newActions, actionIcons, actionsConfig]
  );

  const renderIconItemsLo = useCallback(
    (actionType: IActionsType, dropdownKey: string) => {
      const currentIcon = newActions?.find(item => item.dropdownKey === dropdownKey)?.live_object_id;
      const currentLoId = newActions?.find(item => item.dropdownKey === dropdownKey)?.id;
      const iconLoToPreview = liveObjectsIcons[currentIcon as keyof typeof liveObjectsIcons];
      if (iconLoToPreview) {
        return (
          <ImageActions
            style={{ width: '20px', height: '20px', marginRight: '4px', objectFit: 'contain' }}
            key={`${iconLoToPreview} + ${dropdownKey}`}
            src={liveObjectsBasePath + iconLoToPreview + '.png'}
            alt={iconLoToPreview}
          />
        );
      }
      if (currentLoId) {
        return actionIcons[currentLoId];
      }
      if (!currentIcon) {
        return actionIcons[actionType];
      }
    },
    [newActions, actionIcons, liveObjectsBasePath, blockId, liveObjectsIcons]
  );
  function trimAfterFirstUppercase(str: string) {
    const index = str.slice(1).search(/[A-Z]/) + 1;
    if (index > 0) {
      return str.slice(0, index);
    }
    return str;
  }

  function trimPositionUppercase(str: string) {
    const index = str.slice(1).search(/[A-Z]/) + 1;
    if (index > 0) {
      return str.slice(index);
    }
    return str;
  }

  const renderTitle = useCallback(
    (actionType: string, dropdownKey: string) => {
      if (actionType === 'effects' || actionType === 'awards') return;

      const selectedActionId = newActions?.find(item => item.dropdownKey === dropdownKey)?.id;

      if (
        (actionType === 'drawing' ||
          actionType === 'whiteboard' ||
          actionType === 'live_object' ||
          actionType === 'place_live_objects') &&
        selectedActionId
      ) {
        return ActionButtonEnum[selectedActionId as keyof typeof ActionButtonEnum];
      }
      if (actionType === 'character_moving_actions' && selectedActionId) {
        const position = trimPositionUppercase(selectedActionId);
        return <span style={{ padding: '0 5px' }}>{position}</span>;
      }
      if (selectedActionId) {
        return <span style={{ padding: '0 5px' }}>{selectedActionId}</span>;
      }

      return <span style={{ padding: '0 5px' }}>{ActionButtonEnum[actionType as keyof typeof ActionButtonEnum]}</span>;
    },

    [newActions]
  );

  const renderIconAwardsEffects = useCallback(
    (dropdownKey: string) => {
      let dropdownKeyEffects = dropdownKey;
      dropdownKeyEffects = dropdownKey.replace('awards', 'effects');

      const selectedActionIdAward = newActions?.find(item => item.dropdownKey === dropdownKey)?.id;
      const selectedActionIdEffect = newActions?.find(item => item.dropdownKey === dropdownKeyEffects)?.id;

      const iconAward = selectedActionIdAward && actionsConfig['awards'].find(item => item.id === selectedActionIdAward)?.icon;
      const iconEffect =
        selectedActionIdEffect && actionsConfig['effects'].find(item => item.id === selectedActionIdEffect)?.icon;

      if (iconAward && !iconEffect) {
        return (
          <ImageActions
            style={{ width: '25px', height: '25px', marginRight: '4px' }}
            key={iconAward}
            src={baseActionPaths.actions_media_base_path + iconAward + '.png'}
            alt={iconAward}
          />
        );
      } else if (iconEffect && !iconAward) {
        return (
          <ImageActions
            style={{ width: '25px', height: '25px', marginRight: '4px' }}
            key={iconEffect}
            src={baseActionPaths.actions_media_base_path + iconEffect + '.png'}
            alt={iconEffect}
          />
        );
      } else if (iconEffect && iconAward) {
        return (
          <>
            <ImageActions
              style={{ width: '25px', height: '25px', marginRight: '4px' }}
              key={iconAward}
              src={baseActionPaths.actions_media_base_path + iconAward + '.png'}
              alt={iconAward}
            />
            <ImageActions
              style={{ width: '25px', height: '25px', marginRight: '4px' }}
              key={iconEffect}
              src={baseActionPaths.actions_media_base_path + iconEffect + '.png'}
              alt={iconEffect}
            />
          </>
        );
      }

      return actionIcons['awards'];
    },

    [newActions, actionIcons, actionsConfig]
  );

  const renderTitleAwardsEffects = useCallback(
    (dropdownKey: string) => {
      let dropdownKeyEffects = dropdownKey;
      dropdownKeyEffects = dropdownKey.replace('awards', 'effects');

      const selectedActionIdAward = newActions?.find(item => item.dropdownKey === dropdownKey)?.id;
      const selectedActionIdEffect = newActions?.find(item => item.dropdownKey === dropdownKeyEffects)?.id;

      if (selectedActionIdAward && !selectedActionIdEffect) {
        const title = trimAfterFirstUppercase(selectedActionIdAward);
        return <span style={{ padding: '0 5px' }}>{title}</span>;
      } else if (!selectedActionIdAward && selectedActionIdEffect) {
        const title = trimAfterFirstUppercase(selectedActionIdEffect);
        return <span style={{ padding: '0 5px' }}>{title}</span>;
      } else if (selectedActionIdAward && selectedActionIdEffect) {
        const title = trimAfterFirstUppercase(selectedActionIdAward);
        const titleEffect = trimAfterFirstUppercase(selectedActionIdEffect);
        return (
          <>
            <span style={{ padding: '0 5px' }}>{title}</span>
            <span style={{ padding: '0 5px' }}>{titleEffect}</span>
          </>
        );
      }
      return (
        <>
          <span style={{ padding: '0 5px' }}>{ActionButtonEnum['awards']}</span>
          {/* <span style={{ padding: '0 5px' }}>{ActionButtonEnum['effects']}</span> */}
        </>
      );
    },

    [newActions]
  );
  const renderTitleLo = useCallback(
    (dropdownKey: string) => {
      const currentLoId = newActions?.find(item => item.dropdownKey === dropdownKey)?.live_object_id;
      const currentLoAction = newActions?.find(item => item.dropdownKey === dropdownKey)?.id;
      const currentLoName = liveObjectsIcons[currentLoId as keyof typeof liveObjectsIcons];

      if (currentLoName) {
        return currentLoName;
      }
      if (currentLoAction === 'delete_all_live_objects') {
        return ActionButtonEnum[currentLoAction as keyof typeof ActionButtonEnum];
      }
      if (!currentLoId) {
        return 'Choose LO';
      }
    },

    [newActions, blockId, liveObjectsIcons]
  );

  const handleOpenDropdown = (dropdownKey: string, flag: boolean) => {
    setOpenDropdowns(prevState => ({
      ...prevState,
      [dropdownKey]: flag
    }));
  };
  const draggableStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 'auto',
    opacity: isDragging ? 0.5 : 1
  };
  return (
    <>
      {(!actions?.length && !content) || editing ? (
        <NotesContainer>
          <TextAreaContainer>
            <StyledTextArea rows={2} autoFocus value={newContent} onChange={e => setNewContent(e.target.value)} />
          </TextAreaContainer>
          {!!selectedAction.length && (
            <BlockActions>
              {selectedAction.map((item, idx) => {
                const dropdownKey = `${item}-${idx}`;
                if (item !== 'effects') {
                  return (
                    <NotesBlock key={dropdownKey + blockId}>
                      {ActionEnum[item as keyof typeof ActionEnum]}
                      <Dropdown
                        key={dropdownKey}
                        menu={renderMenuActionsItems(item as IActionsType, idx)}
                        placement="top"
                        overlayClassName={`overly-menu-note-items ${'overly-menu-note-items-' + item} ${item === 'drawing' || item === 'whiteboard' || item === 'live_object' ? 'overly-menu-note-items-columns' : ''}`}
                        className="drop-menu"
                        trigger={['click']}
                        open={!!openDropdowns[dropdownKey]}
                        onOpenChange={flag => handleOpenDropdown(dropdownKey, flag)}
                      >
                        <ButtonAction
                          key={'btn-menu'}
                          onClick={() => {
                            let effectsAction = '';
                            const currentAction = newActions?.find(item => item.dropdownKey === dropdownKey)?.id || '';
                            if (item === 'awards') {
                              effectsAction =
                                newActions.find(item => item.dropdownKey === dropdownKey.replace('awards', 'effects'))?.id || '';
                            }
                            setSelectedActionsItem(prev => [...prev, currentAction, effectsAction]);
                          }}
                        >
                          {item !== 'awards' && renderIcon(item as IActionsType, dropdownKey)}
                          {item === 'awards' && renderIconAwardsEffects(dropdownKey)}
                          {item !== 'awards' && renderTitle(item, dropdownKey)}
                          {item === 'awards' && renderTitleAwardsEffects(dropdownKey)}
                          <ArrowDownIcon open={!!openDropdowns[dropdownKey]} />
                        </ButtonAction>
                      </Dropdown>
                      {!!liveObjects.length &&
                        !!newActions.length &&
                        newActions.find(action => action.dropdownKey === dropdownKey)?.id === 'place_live_objects' && (
                          <Dropdown
                            key={`${dropdownKey} - drop-lo`}
                            menu={renderLoMenuActionsItems(dropdownKey, idx, item as IActionsType)}
                            placement="top"
                            overlayClassName={`overly-menu-note-items overly-menu-note-items-live_object-items`}
                            className="drop-menu-lo"
                            trigger={['click']}
                            open={!!openDropdowns[`${dropdownKey}-drop-lo`]}
                            onOpenChange={flag => handleOpenDropdown(`${dropdownKey}-drop-lo`, flag)}
                          >
                            <ButtonAction
                              key={`btn-menu-item-${dropdownKey}`}
                              onClick={() => {
                                const currentAction =
                                  newActions.find(item => item.dropdownKey === dropdownKey)?.live_object_id || '';
                                setSelectedActionsItem(prev => [...prev, currentAction]);
                              }}
                            >
                              {renderIconItemsLo('place_live_objects' as IActionsType, dropdownKey)}
                              {renderTitleLo(dropdownKey)}
                              <ArrowDownIcon open={!!openDropdowns[`${dropdownKey}-drop-lo`]} />
                            </ButtonAction>
                          </Dropdown>
                        )}
                    </NotesBlock>
                  );
                }
              })}
            </BlockActions>
          )}

          {!!menuItems.length && selectedAction.length <= 4 && (
            <Dropdown
              menu={menuActions}
              placement="top"
              overlayClassName="overly-menu-note"
              className="drop-menu"
              trigger={['click']}
              disabled={!menuItems.length}
            >
              <ButtonAddAction>
                <StyledIcon />
                Add action
                <StyledRightButtonSection>
                  <PlusIcon />
                </StyledRightButtonSection>
              </ButtonAddAction>
            </Dropdown>
          )}

          <StyledCloseCircleIcon
            onClick={() => {
              if (editing) {
                setNewActions(() => initNewActions());
                setNewContent(content);
                setEditing(false);
                setEditableBlockId(null);
              } else {
                handleRemove();
              }
              setActiveBlockId(null);
            }}
          />
          <StyledCheckIcon onClick={handleSaveNote} />
        </NotesContainer>
      ) : (
        <NotesContainer ref={setNodeRef} style={{ ...draggableStyle, minHeight: 'unset' }} {...attributes}>
          <DragIcon {...listeners} className=" drag-icon-absolute cursor-grab" />
          <PenIcon
            className="cursor-pointer pen-icon-absolute"
            onClick={() => {
              setEditableBlockId(blockId);
              setActiveBlockId(blockId);
              setEditing(true);
            }}
          />

          <Popconfirm title="Delete this Action?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={handleRemove}>
            <DeleteFilled className="cursor-pointer delete-icon-absolute" />
          </Popconfirm>
          <BlockActions>
            {content && (
              <pre style={{ whiteSpace: 'pre-wrap' }}>
                <StyledContentPreview className="normal-16 m-0 primary-color">{content}</StyledContentPreview>
              </pre>
            )}

            {newActionsToPreview.map((item, idx) => {
              if (item.action_type !== 'effects') {
                return (
                  <NotesBlock key={item.id + idx}>
                    {ActionEnum[item.action_type as keyof typeof ActionEnum]}
                    <ButtonAction disabled={true}>
                      {item.action_type !== 'live_object' && item.action_type !== 'awards'
                        ? renderIcon(item.action_type as IActionsType, item.dropdownKey)
                        : item.action_type === 'live_object'
                          ? renderIconItemsLo(item.action_type as IActionsType, item.dropdownKey)
                          : item.action_type === 'awards' && renderIconAwardsEffects(item.dropdownKey)}

                      {item.action_type !== 'live_object' && item.action_type !== 'awards'
                        ? renderTitle(item.action_type as IActionsType, item.dropdownKey)
                        : item.action_type === 'live_object'
                          ? renderTitleLo(item.dropdownKey)
                          : item.action_type === 'awards' && renderTitleAwardsEffects(item.dropdownKey)}
                    </ButtonAction>
                  </NotesBlock>
                );
              }
            })}
          </BlockActions>
        </NotesContainer>
      )}
    </>
  );
};
